import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Alert, AlertDescription } from "../ui/alert";
import { ShieldAlert, Lock, Eye, EyeOff, CheckCircle2, ArrowRight } from "lucide-react";
import { User } from "../../types";
import { apiService } from "../../utils/apiService";

interface ChangePasswordModalProps {
  currentUser: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedUser: User) => void;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  currentUser,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [currentPassword, setCurrentPassword] = useState("admin123");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  if (!currentUser) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!newPassword) {
      setError("Please enter a new password.");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    if (newPassword.toLowerCase() === "admin123") {
      setError("Please choose a password different from the default admin123.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiService.changePassword(newPassword, currentPassword);

      if (response && response.error) {
        throw new Error(response.error);
      }

      setSuccessMessage("Password updated successfully!");

      const updatedUser: User = {
        ...currentUser,
        mustChangePassword: false,
        must_change_password: false,
      };

      setTimeout(() => {
        onSuccess(updatedUser);
        onClose();
      }, 1200);
    } catch (err: any) {
      setError(err?.message || "Failed to update password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md bg-background border-border text-foreground shadow-2xl">
        <DialogHeader className="text-left space-y-2">
          <div className="flex items-center space-x-2 text-amber-500">
            <ShieldAlert className="h-6 w-6" />
            <DialogTitle className="text-lg font-semibold tracking-tight text-foreground">
              Security Action Required
            </DialogTitle>
          </div>
          <DialogDescription className="text-sm text-muted-foreground">
            Your account <span className="font-semibold text-foreground">{currentUser.email}</span> is using the default seeded password (<code className="text-xs bg-muted px-1.5 py-0.5 rounded">admin123</code>). Please choose a new secure password before proceeding.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive" className="py-2 text-sm border-red-500/40 bg-red-500/10 text-red-400">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {successMessage && (
          <Alert className="py-2 text-sm border-emerald-500/40 bg-emerald-500/10 text-emerald-400">
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>{successMessage}</AlertDescription>
            </div>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          <div className="space-y-2">
            <Label htmlFor="current-pw" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Current Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="current-pw"
                type="text"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Current default password"
                className="pl-9 bg-muted/40 font-mono text-sm"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-pw" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              New Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="new-pw"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="pl-9 pr-10 bg-muted/40 font-mono text-sm"
                required
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
              >
                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-pw" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Confirm New Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="confirm-pw"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat new password"
                className="pl-9 pr-10 bg-muted/40 font-mono text-sm"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-border mt-5">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isLoading}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Remind me later
            </Button>
            <Button
              type="submit"
              disabled={isLoading || Boolean(successMessage)}
              className="px-5 bg-foreground text-background hover:bg-foreground/90 font-medium"
            >
              {isLoading ? "Updating..." : (
                <span className="flex items-center space-x-1.5">
                  <span>Save Password</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

